import { Helmet } from 'react-helmet-async';

interface PageMetaProps {
  title: string;
  description?: string;
  /** URL absolue de l'image Open Graph (au moins 1200×630 recommandé) */
  image?: string;
  /** Canonique — défaut : window.location.href */
  canonical?: string;
  /**
   * Valeur de la balise robots.
   * Défaut : 'index, follow'
   * Utiliser 'noindex, nofollow' pour les pages privées/auth.
   */
  robots?: string;
  /**
   * og:type — défaut 'website'. Utiliser 'article' pour les articles de blog,
   * 'profile' pour les profils publics.
   */
  ogType?: string;
}

const SITE_NAME = 'BoulesConnect';
/** Host canonique unique (non-www). 301 edge : www → non-www (cf. docs/seo/audit-final.md). */
export const SITE_URL = 'https://boulesconnect.fr';
const DEFAULT_DESCRIPTION =
  'La plateforme de gestion de concours de pétanque : inscrivez votre équipe, gérez vos clubs et suivez les compétitions.';
/** Image Open Graph par défaut (1200×630, générée par scripts/generate-og.mjs). */
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`;

/**
 * Canonical normalisé : host fixe non-www, SANS query string ni hash parasites.
 * - prop absolue (http[s]://…) → conservée telle quelle
 * - prop relative (/chemin)    → préfixée par SITE_URL
 * - sinon                      → SITE_URL + pathname courant (query/hash exclus)
 */
function buildCanonical(canonical?: string): string {
  if (canonical) {
    if (/^https?:\/\//i.test(canonical)) return canonical;
    return `${SITE_URL}${canonical.startsWith('/') ? '' : '/'}${canonical}`;
  }
  if (typeof window === 'undefined') return SITE_URL;
  return `${SITE_URL}${window.location.pathname}`;
}

export function PageMeta({ title, description, image, canonical, robots, ogType }: PageMetaProps) {
  const fullTitle = title === SITE_NAME ? SITE_NAME : `${title} — ${SITE_NAME}`;
  const desc = description ?? DEFAULT_DESCRIPTION;
  const url = buildCanonical(canonical);
  const robotsContent = robots ?? 'index, follow';
  const type = ogType ?? 'website';
  // og:image : image fournie (fiche club/org/concours…) sinon visuel de marque par défaut.
  const ogImage = image ?? DEFAULT_OG_IMAGE;
  const usingDefaultImage = !image;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name='description' content={desc} />
      <meta name='robots' content={robotsContent} />
      {url && <link rel='canonical' href={url} />}

      {/* Open Graph */}
      <meta property='og:title' content={fullTitle} />
      <meta property='og:description' content={desc} />
      <meta property='og:type' content={type} />
      {url && <meta property='og:url' content={url} />}
      <meta property='og:image' content={ogImage} />
      <meta property='og:image:alt' content={fullTitle} />
      {usingDefaultImage && <meta property='og:image:width' content='1200' />}
      {usingDefaultImage && <meta property='og:image:height' content='630' />}
      <meta property='og:site_name' content={SITE_NAME} />
      <meta property='og:locale' content='fr_FR' />

      {/* Twitter Card */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={fullTitle} />
      <meta name='twitter:description' content={desc} />
      <meta name='twitter:image' content={ogImage} />
    </Helmet>
  );
}
